import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { BookingSearchService } from '../../services/booking-search.service';
import {
  BookingSearchPage,
  BookingSearchResult,
} from '../../types/booking-search.type';

@Component({
  selector: 'hospitality-bot-booking-search',
  templateUrl: './booking-search.component.html',
  styleUrls: ['./booking-search.component.scss'],
})
export class BookingSearchComponent implements OnInit, OnChanges, OnDestroy {
  @Input() entityId: string;
  @Input() reservationId: string;
  @Input() label = 'Search Reservation';
  @Input() placeholder =
    'Reservation no, guest name, email, phone or sharer';
  @Input() autoSearch = true;
  @Input() selected: BookingSearchResult | null = null;

  @Output() selectedChange = new EventEmitter<BookingSearchResult | null>();
  @Output() onSelect = new EventEmitter<BookingSearchResult>();

  searchForm: FormGroup;
  results: BookingSearchResult[] = [];
  isSearching = false;
  isLoadingMore = false;
  paginationDisabled = false;

  readonly pageSize = 10;

  private offset = 0;
  private total = 0;
  private loadedCount = 0;
  private destroy$ = new Subject<void>();
  private search$ = new Subject<string>();
  private cancelLoadMore$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private bookingSearchService: BookingSearchService
  ) {
    this.searchForm = this.fb.group({
      search: [''],
    });
  }

  ngOnInit(): void {
    this.search$
      .pipe(
        debounceTime(300),
        switchMap((query: string) => {
          this.cancelLoadMore$.next();
          this.offset = 0;
          this.total = 0;
          this.loadedCount = 0;
          this.paginationDisabled = false;
          this.isLoadingMore = false;
          this.isSearching = true;
          return this.fetchPage(query, 0);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((page) => {
        this.isSearching = false;
        this.results = page.results || [];
        this.applyPageMeta(page);
      });

    this.searchForm
      .get('search')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((query: string) => this.search$.next(query || ''));

    if (this.autoSearch) {
      this.search();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.autoSearch &&
      (changes.reservationId || changes.entityId) &&
      !changes.reservationId?.firstChange &&
      !changes.entityId?.firstChange
    ) {
      this.search();
    }
  }

  search(query = ''): void {
    this.searchForm.get('search')?.setValue(query, { emitEvent: false });
    this.search$.next(query);
  }

  loadMore(): void {
    if (
      this.paginationDisabled ||
      this.isSearching ||
      this.isLoadingMore ||
      !this.entityId
    ) {
      return;
    }

    this.isLoadingMore = true;
    this.offset += this.pageSize;
    this.fetchPage(this.searchForm.get('search')?.value || '', this.offset)
      .pipe(takeUntil(this.cancelLoadMore$), takeUntil(this.destroy$))
      .subscribe(
        (page) => {
          this.results = this.mergeResults(page.results || []);
          this.applyPageMeta(page);
          this.isLoadingMore = false;
        },
        () => {
          this.isLoadingMore = false;
          this.offset = Math.max(0, this.offset - this.pageSize);
        }
      );
  }

  reset(): void {
    this.cancelLoadMore$.next();
    this.selected = null;
    this.results = [];
    this.offset = 0;
    this.total = 0;
    this.loadedCount = 0;
    this.paginationDisabled = false;
    this.isLoadingMore = false;
    this.searchForm.get('search')?.setValue('', { emitEvent: false });
    this.selectedChange.emit(null);
  }

  isSelected(res: BookingSearchResult): boolean {
    if (!this.selected) return false;
    return this.selected.id === res.id && this.selected.guest === res.guest;
  }

  selectResult(res: BookingSearchResult): void {
    this.selected = res;
    this.selectedChange.emit(res);
    this.onSelect.emit(res);
  }

  get hasTypedQuery(): boolean {
    return !!(this.searchForm.get('search')?.value || '').trim();
  }

  ngOnDestroy(): void {
    this.cancelLoadMore$.next();
    this.cancelLoadMore$.complete();
    this.destroy$.next();
    this.destroy$.complete();
    this.search$.complete();
  }

  private fetchPage(query: string, offset: number) {
    const typed = (query || '').trim();
    const useKeyId = !typed;
    const searchValue = typed || this.reservationId || '';
    return this.bookingSearchService.searchBookings(
      this.entityId,
      searchValue,
      { useKeyId, offset, limit: this.pageSize }
    );
  }

  private applyPageMeta(page: BookingSearchPage): void {
    this.loadedCount += page.recordCount || 0;
    this.total = page.total || 0;
    this.paginationDisabled =
      page.recordCount < this.pageSize ||
      (this.total > 0 && this.loadedCount >= this.total);
  }

  private mergeResults(
    incoming: BookingSearchResult[]
  ): BookingSearchResult[] {
    const seen = new Set(
      this.results.map((row) => this.rowKey(row))
    );
    const extra = incoming.filter((row) => {
      const key = this.rowKey(row);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return [...this.results, ...extra];
  }

  private rowKey(row: BookingSearchResult): string {
    return `${row.id || row.res}|${row.guest}|${row.role || ''}`;
  }
}
